/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/purchase_order_line_items-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-purchase_order_line_items-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/purchase_order_line_items-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, purchase_order_id, material_id, job_id, lot_id, area_id, status_id, trig_material_vendor_id, trig_material_vendor_sku, line_number, source_area_field_junction_id
 * - Sortable fields: created_at, updated_at, line_number
 * - Required fields: purchase_order_id, material_id, quantity, unit_cost, total_cost
 * - Readonly fields: id, created_at, updated_at, trig_status_name, trig_status_code, trig_status_color, trig_lot_number, trig_lot_address, trig_lot_square_footage, trig_lot_phase_name, trig_area_name, trig_area_sqft, trig_area_type_code, trig_material_color, trig_material_name, trig_material_unit_of_measure, trig_material_department, trig_material_code, trig_material_manufacturer, trig_material_vendor_id, trig_material_vendor_name, trig_material_vendor_sku
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/purchase_order_line_items-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-purchase_order_line_items-api-client.ts (API client)
 * - client/api/generated_schemas/purchase_order_line_items.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-10-26T15:51:14.830Z
 * Source config: config/entities/purchase_order_line_items-entity-config.ts
 * Public fields: 52/52 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PurchaseOrderLineItemsClient, PurchaseOrderLineItemsQueryOptions } from '../generated_clients/generated-purchase_order_line_items-api-client.js';
import {
  PurchaseOrderLineItems,
  PurchaseOrderLineItemsInsert,
  PurchaseOrderLineItemsUpdate,
  PurchaseOrderLineItemsFieldConfig
} from '../generated_schemas/generated-purchase_order_line_items-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/purchase_order_line_items_business_hooks/

// Query keys with enhanced caching strategy
export const purchaseOrderLineItemsKeys = {
  all: ['purchase_order_line_items'] as const,
  lists: () => [...purchaseOrderLineItemsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...purchaseOrderLineItemsKeys.lists(), { filters }] as const,
  details: () => [...purchaseOrderLineItemsKeys.all, 'detail'] as const,
  detail: (id: string) => [...purchaseOrderLineItemsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...purchaseOrderLineItemsKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...purchaseOrderLineItemsKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...purchaseOrderLineItemsKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...purchaseOrderLineItemsKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const usePurchaseOrderLineItemsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new PurchaseOrderLineItemsClient();
};

/**
 * Hook to get all purchase_order_line_items with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with purchase_order_line_items data
 */
export const usePurchaseOrderLineItemsList = (options?: PurchaseOrderLineItemsQueryOptions) => {
  const client = usePurchaseOrderLineItemsClient();

  return useQuery({
    queryKey: purchaseOrderLineItemsKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
    // Always reconcile with the server on mount, even when persisted/cached
    // data is restored (field PWA offline cache) or a cross-device write
    // happened elsewhere. Cached data still paints instantly; the refetch
    // corrects it. Offline, the refetch simply fails and the cache is kept.
    // Nobody should ever see stale operational data. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * Hook to get a single purchase_order_line_item by ID
 *
 * @param id - The purchase_order_line_item ID
 * @returns React Query result with single purchase_order_line_item data
 */
export const usePurchaseOrderLineItems = (id: string) => {
  const client = usePurchaseOrderLineItemsClient();

  return useQuery({
    queryKey: purchaseOrderLineItemsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
    // Always reconcile with the server on mount so a reload / navigation never
    // paints a stale restored (persisted) detail snapshot. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/purchase_order_line_items
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/purchase_order_line_items_business_hooks/
 */

/**
 * Hook to create a new purchase_order_line_item
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: purchase_order_id, material_id, quantity, unit_cost, total_cost
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at, trig_status_name, trig_status_code, trig_status_color, trig_lot_number, trig_lot_address, trig_lot_square_footage, trig_lot_phase_name, trig_area_name, trig_area_sqft, trig_area_type_code, trig_material_color, trig_material_name, trig_material_unit_of_measure, trig_material_department, trig_material_code, trig_material_manufacturer, trig_material_vendor_id, trig_material_vendor_name, trig_material_vendor_sku
 *
 * @returns React Query mutation for creating purchase_order_line_item
 */
export const useCreatePurchaseOrderLineItems = () => {
  const client = usePurchaseOrderLineItemsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: PurchaseOrderLineItemsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderLineItemsKeys.all });
    },
  });
};

/**
 * Hook to update an existing purchase_order_line_item
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at, trig_status_name, trig_status_code, trig_status_color, trig_lot_number, trig_lot_address, trig_lot_square_footage, trig_lot_phase_name, trig_area_name, trig_area_sqft, trig_area_type_code, trig_material_color, trig_material_name, trig_material_unit_of_measure, trig_material_department, trig_material_code, trig_material_manufacturer, trig_material_vendor_id, trig_material_vendor_name, trig_material_vendor_sku
 *
 * @returns React Query mutation for updating purchase_order_line_item
 */
export const useUpdatePurchaseOrderLineItems = () => {
  const client = usePurchaseOrderLineItemsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PurchaseOrderLineItemsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderLineItemsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: purchaseOrderLineItemsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a purchase_order_line_item
 *
 * @returns React Query mutation for deleting purchase_order_line_item
 */
export const useDeletePurchaseOrderLineItems = () => {
  const client = usePurchaseOrderLineItemsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderLineItemsKeys.all });
    },
  });
};

/**
 * Hook to count purchase_order_line_items with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, purchase_order_id, material_id, job_id, lot_id, area_id, status_id, trig_material_vendor_id, trig_material_vendor_sku, line_number, source_area_field_junction_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const usePurchaseOrderLineItemsCount = (filter?: Partial<Record<typeof PurchaseOrderLineItemsFieldConfig.filterable[number], any>>) => {
  const client = usePurchaseOrderLineItemsClient();
  
  return useQuery({
    queryKey: purchaseOrderLineItemsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const usePurchaseOrderLineItemsFieldConfig = () => {
  return PurchaseOrderLineItemsFieldConfig;
};